import React, { useState, useEffect, useContext } from "react";
import { postData } from "../../../../services/Fetchnodeservices";
import { ThemeContext } from "../../../../context/ThemeContext";

// ─────────────────────────────────────────────────────────────────────────────
// DELIVERY HISTORY TABLE COMPONENT - Updated with Theme Context
// ─────────────────────────────────────────────────────────────────────────────
const DeliveryHistoryTable = ({ driverId }) => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (driverId) {
      fetchDeliveryHistory();
    }
  }, [driverId, currentPage]);

  const fetchDeliveryHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await postData("drivers/Ridersorderhistory", {
        delivery_person_id: driverId
      });
      console.log("Delivery History Response:", response);

      if (response?.status === true && Array.isArray(response?.data)) {
        setOrders(response.data);
        setTotalPages(Math.ceil(response.data.length / itemsPerPage));
      } else {
        setOrders([]);
        setError("No delivery history found");
      }
    } catch (err) {
      console.error("Error fetching delivery history:", err);
      setError("Failed to load delivery history");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      delivered: { bg: `${theme.success}1a`, color: theme.success, label: "Delivered" },
      completed: { bg: `${theme.success}1a`, color: theme.success, label: "Completed" },
      pending: { bg: `${theme.warning}1a`, color: theme.warning, label: "Pending" },
      placed: { bg: `${theme.primary}1a`, color: theme.primary, label: "Placed" },
      accepted: { bg: `${theme.primary}1a`, color: theme.primary, label: "Accepted" },
      processing: { bg: `${theme.primary}1a`, color: theme.primary, label: "Processing" },
      cancelled: { bg: `${theme.error || theme.danger}1a`, color: theme.error || theme.danger, label: "Cancelled" },
      confirmed: { bg: `${theme.primary}1a`, color: theme.primary, label: "Confirmed" },
      shipped: { bg: `${theme.primary}1a`, color: theme.primary, label: "Shipped" },
      out_for_delivery: { bg: `${theme.primary}1a`, color: theme.primary, label: "Out for Delivery" },
      ready: { bg: `${theme.success}1a`, color: theme.success, label: "Ready" },
      preparing: { bg: `${theme.primary}1a`, color: theme.primary, label: "Preparing" },
      dropped: { bg: `${theme.textMuted}1a`, color: theme.textMuted, label: "Dropped" }
    };
    const s = statusMap[status?.toLowerCase()] || { bg: `${theme.textMuted}1a`, color: theme.textMuted, label: status || "Unknown" };
    return (
      <span style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "11px",
        fontWeight: 600,
        background: s.bg,
        color: s.color
      }}>
        {s.label}
      </span>
    );
  };

  const getPayStatusBadge = (status) => {
    const isPaid = status?.toLowerCase() === "paid";
    return (
      <span style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "10px",
        fontWeight: 600,
        background: isPaid ? `${theme.success}1a` : `${theme.warning}1a`,
        color: isPaid ? theme.success : theme.warning
      }}>
        {isPaid ? "Paid" : "Pending"}
      </span>
    );
  };

  const paginatedOrders = orders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        padding: "40px",
        color: theme.textMuted
      }}>
        <div style={{ 
          width: "30px", 
          height: "30px", 
          border: `3px solid ${theme.border}`, 
          borderTopColor: theme.primary, 
          borderRadius: "50%", 
          animation: "spin 1s linear infinite",
          marginRight: "12px"
        }} />
        Loading delivery history...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: "center", 
        padding: "40px",
        color: theme.textMuted
      }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>📦</div>
        <p style={{ color: theme.textSecondary }}>{error}</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={{ 
        textAlign: "center", 
        padding: "60px 20px",
        background: theme.surface,
        borderRadius: "16px",
        border: `1px solid ${theme.border}`
      }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🚚</div>
        <h3 style={{ color: theme.textPrimary, marginBottom: "8px" }}>No Deliveries Yet</h3>
        <p style={{ color: theme.textMuted }}>This driver hasn't completed any deliveries</p>
      </div>
    );
  }

  return (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "16px",
      overflow: "hidden"
    }}>
      <div style={{
        padding: "16px 20px",
        borderBottom: `1px solid ${theme.border}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
        background: theme.surfaceLighter
      }}>
        <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: theme.textPrimary }}>
          📋 Delivery History
        </h3>
        <span style={{ fontSize: "12px", color: theme.textMuted }}>
          Total Deliveries: <strong style={{ color: theme.textPrimary }}>{orders.length}</strong>
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          minWidth: "1100px",
          fontSize: "13px"
        }}>
          <thead>
            <tr style={{
              background: theme.surfaceLighter,
              borderBottom: `1px solid ${theme.border}`
            }}>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Order ID</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>User</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Restaurant</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Amount</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Payment</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Payment Mode</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Cash Status</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Restro Status</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Driver Status</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order, idx) => (
              <tr
                key={order.id || idx}
                style={{
                  borderBottom: idx < paginatedOrders.length - 1 ? `1px solid ${theme.border}` : "none",
                  transition: "background 0.2s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || `${theme.primary}08`}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                <td style={{ padding: "12px 16px", fontWeight: 600, fontFamily: "monospace", color: theme.primary, fontSize: "12px" }}>
                  #{order.id}
                </td>
                <td style={{ padding: "12px 16px", color: theme.textSecondary }}>
                  {order.receiver_name || `User ${order.userid}`}
                </td>
                <td style={{ padding: "12px 16px", color: theme.textSecondary }}>
                  {order.organisation_name || order.designation || "N/A"}
                </td>
                <td style={{ padding: "12px 16px", fontWeight: 600, color: theme.success }}>
                  {formatCurrency(order.final_amount || order.price)}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  {getPayStatusBadge(order.pay_status)}
                </td>
                
                {/* Payment Mode */}
                <td style={{ padding: "12px 16px", color: theme.textSecondary }}>
                  {order.payment_received_mode || "-"}
                </td>
                
                {/* Cash Status */}
                <td style={{ padding: "12px 16px" }}>
                  {order.payment_received_mode === "CASH" ? (
                    (() => {
                      if (order.is_driver_deposit_cash == 1 && order.payment_verified_by_admin == 1) {
                        return <span style={{ color: theme.success, fontWeight: 600 }}>✅ Deposited & Verified</span>;
                      } else if (order.is_driver_deposit_cash == 1 && order.payment_verified_by_admin == 0) {
                        return <span style={{ color: theme.warning, fontWeight: 600 }}>⏳ Pending Verification</span>;
                      } else {
                        return <span style={{ color: theme.error || theme.danger, fontWeight: 500 }}>⚠️ Not Deposited</span>;
                      }
                    })()
                  ) : (
                    <span style={{ color: theme.textMuted }}>—</span>
                  )}
                </td>

                <td style={{ padding: "12px 16px" }}>
                  {getStatusBadge(order.order_status_restro)}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  {getStatusBadge(order.order_status_driver)}
                </td>
                <td style={{ padding: "12px 16px", fontSize: "11px", color: theme.textMuted }}>
                  {formatDateTime(order.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{
          padding: "12px 20px",
          borderTop: `1px solid ${theme.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          background: theme.surfaceLighter
        }}>
          <span style={{ fontSize: "12px", color: theme.textMuted }}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, orders.length)} of {orders.length} deliveries
          </span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === 1 ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "12px",
                transition: "all 0.2s"
              }}
            >
              Previous
            </button>
            <span style={{
              padding: "6px 12px",
              borderRadius: "6px",
              background: theme.primary,
              color: "#fff",
              fontSize: "12px",
              fontWeight: 600
            }}>
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === totalPages ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "12px",
                transition: "all 0.2s"
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default DeliveryHistoryTable;